<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Send Finished Goods to Inventory
            </v-card-title>
            <v-card-text class="pt-4">
              <v-autocomplete
                v-model="form.production_master_id"
                :items="eligibleProductions"
                :item-title="productionTitle"
                item-value="id"
                label="Approved Production *"
                density="compact"
                variant="outlined"
                clearable
                :disabled="isEdit"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="changeProduction"
              />

              <v-autocomplete
                v-model="form.warehouse_id"
                :items="warehouses"
                item-title="name"
                item-value="id"
                label="Destination Inventory Warehouse *"
                density="compact"
                variant="outlined"
                clearable
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
              />

              <v-date-input
                v-model="form.delivery_date"
                label="Delivery Date *"
                input-format="dd/mm/yyyy"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                density="compact"
                variant="outlined"
              />

              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                rows="3"
                density="compact"
                variant="outlined"
              />

              <v-alert type="info" density="compact" variant="tonal">
                Stock is not increased here. Inventory receives this delivery in the next step; batch, manufacturing/expiry dates and full production cost are carried automatically.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Finished-Goods Delivery Lines
            </v-card-title>
            <v-card-text class="pt-4">
              <v-table density="compact" class="border-sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Finished Product</th>
                    <th>Batch</th>
                    <th>Manufactured</th>
                    <th>Expiry</th>
                    <th class="text-end">Unit Cost</th>
                    <th class="text-end">Available</th>
                    <th style="width: 145px">Delivery Qty</th>
                    <th style="width: 135px">Sales Rate</th>
                    <th style="width: 45px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cart" :key="item.production_detail_id">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div class="font-weight-medium">{{ item.product_code }} - {{ item.product_name }}</div>
                      <div class="text-caption text-medium-emphasis">{{ item.unit_name }}</div>
                    </td>
                    <td>{{ item.batch_no || '-' }}</td>
                    <td>{{ dateOnly(item.manufacturing_date) || '-' }}</td>
                    <td>{{ dateOnly(item.expiry_date) || '-' }}</td>
                    <td class="text-end">{{ number(item.total_unit_cost, 6) }}</td>
                    <td class="text-end">{{ number(item.available_quantity) }}</td>
                    <td>
                      <v-text-field
                        v-model.number="item.quantity"
                        type="number"
                        min="0.0001"
                        :max="item.available_quantity"
                        step="0.0001"
                        density="compact"
                        variant="outlined"
                        hide-details
                        @update:model-value="clampQuantity(item)"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="item.sales_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                    </td>
                    <td>
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        color="error"
                        variant="text"
                        @click="cart.splice(index, 1)"
                      />
                    </td>
                  </tr>
                  <tr v-if="cart.length === 0">
                    <td colspan="10" class="text-center text-medium-emphasis py-6">
                      Select an approved completed Production with undelivered quantity.
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="cart.length">
                  <tr class="font-weight-bold">
                    <td colspan="7" class="text-end">Total delivery quantity</td>
                    <td>{{ number(totalQuantity) }}</td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </v-table>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="tonal" :disabled="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" @click="submit(1)">Submit for Approval</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const route = useRoute();
const router = useRouter();

const deliveryId = route.params.id || route.query.id || null;
const isEdit = Boolean(deliveryId);
const saving = ref(false);
const cart = ref([]);

const form = reactive({
  production_master_id: null,
  warehouse_id: null,
  delivery_date: new Date(),
  remarks: "",
});

const productions = computed(() => store.getters.getPrdProductionList || []);
const deliveries = computed(() => store.getters.getPrdDeliveryList || []);
const warehouses = computed(() => store.getters.getWarehouseList || []);

const eligibleProductions = computed(() => productions.value.filter((production) => {
  const approved = String(production.approval_status || "").toLowerCase() === "approved";
  return approved && Boolean(production.posted_at) && (production.details || []).some(
    (detail) => availableForDetail(detail.id, detail) > 0.0001
  );
}));

const totalQuantity = computed(() => cart.value.reduce((sum, item) => sum + Number(item.quantity || 0), 0));

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function productionTitle(item) {
  return `${item.code} · ${item.production_date || ""} · ${String(item.workflow_status || "").replaceAll("_", " ")}`;
}

function pendingForDetail(productionDetailId) {
  return deliveries.value
    .filter((delivery) => Number(delivery.id) !== Number(deliveryId))
    .filter((delivery) => !delivery.posted_at && Number(delivery.status) === 1)
    .filter((delivery) => String(delivery.approval_status || "") !== "Cancelled")
    .flatMap((delivery) => delivery.details || [])
    .filter((detail) => Number(detail.production_detail_id) === Number(productionDetailId))
    .reduce((sum, detail) => sum + Number(detail.quantity || 0), 0);
}

function availableForDetail(id, detail) {
  return Math.max(
    0,
    Number(detail.actual_quantity ?? detail.quantity ?? 0)
      - Number(detail.delivery_quantity || 0)
      - pendingForDetail(id)
  );
}

function mapDetail(detail) {
  const available = availableForDetail(detail.id, detail);
  return {
    production_detail_id: detail.id,
    product_id: detail.product_id,
    product_code: detail.product?.code || "",
    product_name: detail.product?.name || "",
    unit_name: detail.product?.unit?.name || "",
    batch_no: detail.batch_no,
    manufacturing_date: detail.manufacturing_date,
    expiry_date: detail.expiry_date,
    total_unit_cost: Number(detail.total_unit_cost || 0),
    available_quantity: available,
    quantity: available,
    sales_rate: 0,
  };
}

function changeProduction(id) {
  const production = productions.value.find((item) => Number(item.id) === Number(id));
  cart.value = production
    ? (production.details || []).map(mapDetail).filter((detail) => detail.available_quantity > 0.0001)
    : [];
  if (production?.production_date && new Date(form.delivery_date) < new Date(production.production_date)) {
    form.delivery_date = new Date(production.production_date);
  }
}

function clampQuantity(item) {
  item.quantity = Math.min(
    Math.max(0, Number(item.quantity || 0)),
    Number(item.available_quantity || 0)
  );
  item.sales_rate = Math.max(0, Number(item.sales_rate || 0));
}

function validate() {
  if (!form.production_master_id || !form.warehouse_id || !form.delivery_date) {
    return "Approved Production, destination warehouse and delivery date are required.";
  }
  if (!cart.value.length) return "At least one finished-product line is required.";
  if (cart.value.some((item) => Number(item.quantity || 0) <= 0)) {
    return "Every selected finished product must have a delivery quantity greater than zero.";
  }
  return null;
}

async function submit(status) {
  const validationMessage = validate();
  if (validationMessage) {
    toast.warning(validationMessage);
    return;
  }

  const payload = {
    id: deliveryId,
    production_master_id: form.production_master_id,
    warehouse_id: form.warehouse_id,
    delivery_date: formattedDate(form.delivery_date),
    status,
    remarks: form.remarks || null,
    cart: cart.value.map((item) => ({
      production_detail_id: item.production_detail_id,
      product_id: item.product_id,
      quantity: Number(item.quantity),
      sales_rate: Number(item.sales_rate || 0),
    })),
  };

  saving.value = true;
  try {
    const response = await store.dispatch(isEdit ? "updatePrdDelivery" : "savePrdDelivery", payload);
    toast.success(response?.data?.message || "Finished-goods delivery saved.");
    router.push({ name: "delivery-list" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const text = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message;
    toast.error(text || "Finished-goods delivery could not be saved.");
  } finally {
    saving.value = false;
  }
}

async function loadEditData() {
  const response = await store.dispatch("getPrdDeliveryById", deliveryId);
  const data = response?.data || response;
  if (data.production && !productions.value.some((item) => Number(item.id) === Number(data.production.id))) {
    store.commit("SET_PRD_PRODUCTION_LIST", [data.production, ...productions.value]);
  }
  form.production_master_id = data.production_master_id;
  form.warehouse_id = data.warehouse_id;
  form.delivery_date = data.delivery_date ? new Date(data.delivery_date) : new Date();
  form.remarks = data.remarks || "";

  cart.value = (data.details || []).map((detail) => {
    const productionDetail = detail.production_detail || {};
    const available = Math.max(
      Number(detail.quantity || 0),
      Number(productionDetail.actual_quantity || 0)
        - Number(productionDetail.delivery_quantity || 0)
        - pendingForDetail(productionDetail.id)
    );
    return {
      production_detail_id: detail.production_detail_id,
      product_id: detail.product_id,
      product_code: detail.product?.code || "",
      product_name: detail.product?.name || "",
      unit_name: detail.product?.unit?.name || "",
      batch_no: detail.batch_no || productionDetail.batch_no,
      manufacturing_date: detail.manufacturing_date || productionDetail.manufacturing_date,
      expiry_date: detail.expiry_date || productionDetail.expiry_date,
      total_unit_cost: Number(detail.rate || 0),
      available_quantity: available,
      quantity: Number(detail.quantity || 0),
      sales_rate: Number(detail.sales_rate || 0),
    };
  });
}

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch("getPrdProductionList"),
      store.dispatch("getPrdDeliveryList"),
      store.dispatch("getWarehouseList"),
    ]);
    if (isEdit) await loadEditData();
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production delivery data could not be loaded.");
  }
});
</script>
